import JSZip from 'jszip';

/**
 * Rows of an Excel Binary Workbook (`.xlsb`), one at a time.
 *
 * Users re-save the AS/400 daily reports as `.xlsb` to shrink them, but the
 * results are still millions of rows: a 62 MB ITEMSUSPE4 inflates to eight
 * 367 MB sheets. A whole-workbook parser (SheetJS) holds every cell as an
 * object and never finishes, so each sheet's BIFF12 records are inflated
 * chunk by chunk and handed over row by row; nothing but the shared-string
 * table outlives its row.
 *
 * Values only: numbers come back as numbers (no number formats applied),
 * formulas as their cached value, errors and blanks as ''.
 */

/** Return false to skip the rest of this sheet. */
export type XlsbRowVisitor = (row: unknown[], rowIndex: number) => boolean;

/** Visitor for a sheet's rows, or null to skip the sheet unread. */
export type XlsbSheetOpener = (sheetName: string) => XlsbRowVisitor | null;

const WORKBOOK_PART = 'xl/workbook.bin';
const WORKBOOK_RELS_PART = 'xl/_rels/workbook.bin.rels';
const SHARED_STRINGS_PART = 'xl/sharedStrings.bin';

/** [MS-XLSB] 2.3.2 record types this reader acts on. */
const RT = {
  rowHdr: 0x00,
  cellBlank: 0x01,
  cellRk: 0x02,
  cellError: 0x03,
  cellBool: 0x04,
  cellReal: 0x05,
  cellSt: 0x06,
  cellIsst: 0x07,
  fmlaString: 0x08,
  fmlaNum: 0x09,
  fmlaBool: 0x0a,
  fmlaError: 0x0b,
  shortBlank: 0x0c,
  shortRk: 0x0d,
  shortError: 0x0e,
  shortBool: 0x0f,
  shortReal: 0x10,
  shortSt: 0x11,
  shortIsst: 0x12,
  sstItem: 0x13,
  cellRString: 0x3e,
  endSheetData: 0x92,
  bundleSh: 0x9c,
} as const;

/** [MS-XLSB] 2.2 Excel stores at most 16,384 columns (A to XFD). */
const MAX_COLUMN = 0x3fff;

/**
 * Longest partial record carried between chunks. A sheet's largest record is
 * a 32,767-character cell (~64 KB), so a record still incomplete past this
 * has a corrupt size; buffering toward it would only burn memory.
 */
const MAX_CARRY_BYTES = 1 << 20;

interface XlsbRecord {
  type: number;
  /** Offset of the record's payload. */
  start: number;
}

/**
 * Steps through the whole records in a chunk without allocating per record.
 * [MS-XLSB] 2.1.4: the type is a 1–2 byte and the size a 1–4 byte 7-bit
 * varint.
 */
class RecordCursor implements XlsbRecord {
  type = 0;
  start = 0;
  /** Offset of the first record not yet stepped onto. */
  offset = 0;
  private value = 0;

  constructor(private readonly bytes: Uint8Array) {}

  /** Step onto the next record; false when only a partial one remains. */
  next(): boolean {
    const sizeAt = this.varint(this.offset, 2);
    if (sizeAt < 0) return false;
    const type = this.value;
    const start = this.varint(sizeAt, 4);
    if (start < 0 || start + this.value > this.bytes.length) return false;
    this.type = type;
    this.start = start;
    this.offset = start + this.value;
    return true;
  }

  /** Read a varint into `value`; returns the offset after it, or -1. */
  private varint(at: number, maxBytes: number): number {
    let value = 0;
    for (let index = 0; index < maxBytes; index += 1) {
      const byte = this.bytes[at + index];
      if (byte === undefined) return -1;
      value |= (byte & 0x7f) << (7 * index);
      if ((byte & 0x80) === 0) return this.settle(value, at + index + 1);
    }
    return this.settle(value, at + maxBytes);
  }

  private settle(value: number, next: number): number {
    this.value = value;
    return next;
  }
}

/**
 * Walk the complete records in `bytes`; `visit` returns false to stop.
 * Returns the offset of the first record not yet visited (a partial one at
 * the end of a chunk, which the caller carries over).
 */
function forEachRecord(
  bytes: Uint8Array,
  visit: (record: XlsbRecord) => boolean,
): number {
  const cursor = new RecordCursor(bytes);
  while (cursor.next() && visit(cursor)) {
    // visit does the work
  }
  return cursor.offset;
}

const utf16 = new TextDecoder('utf-16le');

/** [MS-XLSB] 2.5.168 XLWideString: a uint32 count, then UTF-16LE. */
function wideString(bytes: Uint8Array, view: DataView, at: number): string {
  const count = view.getUint32(at, true);
  if (count === 0 || count === 0xffffffff) return '';
  const start = at + 4;
  return utf16.decode(bytes.subarray(start, start + count * 2));
}

const rkScratch = new DataView(new ArrayBuffer(8));

/** [MS-XLSB] 2.5.122 RkNumber: a 30-bit int or the top of a double, maybe ×100. */
function rkNumber(view: DataView, at: number): number {
  const raw = view.getInt32(at, true);
  let value: number;
  if (raw & 0x02) {
    value = raw >> 2;
  } else {
    rkScratch.setInt32(0, 0, true);
    rkScratch.setInt32(4, raw & ~0x03, true);
    value = rkScratch.getFloat64(0, true);
  }
  return raw & 0x01 ? value / 100 : value;
}

function viewOf(bytes: Uint8Array): DataView {
  return new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
}

/** Reads the value of a cell record whose value sits at `at`. */
type CellReader = (
  bytes: Uint8Array,
  view: DataView,
  at: number,
  strings: readonly string[],
) => unknown;

const readBlank: CellReader = () => '';
const readRk: CellReader = (_bytes, view, at) => rkNumber(view, at);
const readReal: CellReader = (_bytes, view, at) => view.getFloat64(at, true);
const readBool: CellReader = (bytes, _view, at) => bytes[at] !== 0;
const readString: CellReader = (bytes, view, at) => wideString(bytes, view, at);
// RichStr: a flags byte, then the text.
const readRichString: CellReader = (bytes, view, at) =>
  wideString(bytes, view, at + 1);
const readSharedString: CellReader = (_bytes, view, at, strings) =>
  strings[view.getUint32(at, true)] ?? '';

/** Cell record types and how to read them; errors read as blanks. */
const CELL_READERS = new Map<number, CellReader>([
  [RT.cellBlank, readBlank],
  [RT.shortBlank, readBlank],
  [RT.cellError, readBlank],
  [RT.shortError, readBlank],
  [RT.fmlaError, readBlank],
  [RT.cellRk, readRk],
  [RT.shortRk, readRk],
  [RT.cellReal, readReal],
  [RT.shortReal, readReal],
  [RT.fmlaNum, readReal],
  [RT.cellBool, readBool],
  [RT.shortBool, readBool],
  [RT.fmlaBool, readBool],
  [RT.cellSt, readString],
  [RT.shortSt, readString],
  [RT.fmlaString, readString],
  [RT.cellRString, readRichString],
  [RT.cellIsst, readSharedString],
  [RT.shortIsst, readSharedString],
]);

function parseSharedStrings(bytes: Uint8Array): string[] {
  const view = viewOf(bytes);
  const strings: string[] = [];
  forEachRecord(bytes, (record) => {
    if (record.type === RT.sstItem) {
      // RichStr: a flags byte, then the text.
      strings.push(wideString(bytes, view, record.start + 1));
    }
    return true;
  });
  return strings;
}

/** Sheet names and their relationship ids, in workbook order. */
function parseWorkbookSheets(
  bytes: Uint8Array,
): { name: string; relId: string }[] {
  const view = viewOf(bytes);
  const sheets: { name: string; relId: string }[] = [];
  forEachRecord(bytes, (record) => {
    if (record.type !== RT.bundleSh) return true;
    // hsState (4), iTabID (4), strRelID (nullable), strName.
    const relAt = record.start + 8;
    const relCount = view.getUint32(relAt, true);
    const nameAt = relAt + 4 + (relCount === 0xffffffff ? 0 : relCount * 2);
    sheets.push({
      name: wideString(bytes, view, nameAt),
      relId: wideString(bytes, view, relAt),
    });
    return true;
  });
  return sheets;
}

function relationshipTargets(relsXml: string): Map<string, string> {
  const targets = new Map<string, string>();
  for (const match of relsXml.matchAll(/<Relationship\b[^>]*>/g)) {
    const tag = match[0];
    const id = /\sId="([^"]*)"/.exec(tag)?.[1];
    const target = /\sTarget="([^"]*)"/.exec(tag)?.[1];
    if (id && target) targets.set(id, target);
  }
  return targets;
}

function sheetPath(target: string): string {
  return target.startsWith('/') ? target.slice(1) : `xl/${target}`;
}

/** Turns one sheet's record stream into rows. */
class SheetRows {
  private row: unknown[] = [];
  private rowIndex = -1;
  private column = -1;
  private stopped = false;

  constructor(
    private readonly strings: readonly string[],
    private readonly visit: XlsbRowVisitor,
  ) {}

  get done(): boolean {
    return this.stopped;
  }

  /** Consume the whole records in `bytes`; returns how many bytes were used. */
  take(bytes: Uint8Array): number {
    const view = viewOf(bytes);
    return forEachRecord(bytes, ({ type, start }) => {
      this.record(bytes, view, type, start);
      return !this.stopped;
    });
  }

  /**
   * Flush the last row. `leftover` bytes after the last whole record mean the
   * sheet was cut short, unless the visitor already stopped reading.
   */
  finish(leftover: number): void {
    if (leftover > 0 && !this.stopped) {
      throw new Error('The sheet ends partway through a record.');
    }
    this.flush();
  }

  private flush(): void {
    if (this.stopped || this.rowIndex < 0) return;
    if (!this.visit(this.row, this.rowIndex)) this.stopped = true;
    this.row = [];
    this.rowIndex = -1;
  }

  private record(
    bytes: Uint8Array,
    view: DataView,
    type: number,
    start: number,
  ): void {
    if (type === RT.rowHdr) {
      this.flush();
      this.rowIndex = view.getUint32(start, true);
      this.column = -1;
      return;
    }
    if (type === RT.endSheetData) {
      this.flush();
      return;
    }
    const read = CELL_READERS.get(type);
    if (read) this.cell(bytes, view, type, start, read);
  }

  private cell(
    bytes: Uint8Array,
    view: DataView,
    type: number,
    start: number,
    read: CellReader,
  ): void {
    // Full cells open with a 4-byte column; short ones follow the last cell.
    const short = type >= RT.shortBlank && type <= RT.shortIsst;
    const value = read(bytes, view, start + (short ? 4 : 8), this.strings);
    this.column = short ? this.column + 1 : view.getUint32(start, true);
    if (this.column > MAX_COLUMN) {
      throw new Error(
        `Cell column ${this.column} is past Excel's last column.`,
      );
    }
    while (this.row.length < this.column) this.row.push('');
    this.row[this.column] = value;
  }
}

/**
 * jszip's chunked reader of one entry. Shipped in every 3.x build but absent
 * from its type definitions (see `peekWorkbook`).
 */
interface StreamingZipObject extends JSZip.JSZipObject {
  internalStream(type: 'uint8array'): JSZip.JSZipStreamHelper<Uint8Array>;
}

function canStream(entry: JSZip.JSZipObject): entry is StreamingZipObject {
  return (
    'internalStream' in entry && typeof entry.internalStream === 'function'
  );
}

function joinBytes(head: Uint8Array, tail: Uint8Array): Uint8Array {
  if (head.length === 0) return tail;
  const joined = new Uint8Array(head.length + tail.length);
  joined.set(head);
  joined.set(tail, head.length);
  return joined;
}

function scanSheet(entry: JSZip.JSZipObject, rows: SheetRows): Promise<void> {
  if (!canStream(entry)) {
    return entry.async('uint8array').then((bytes) => {
      rows.finish(bytes.length - rows.take(bytes));
    });
  }
  return new Promise((resolve, reject) => {
    let carry = new Uint8Array(0);
    let settled = false;
    const stream = entry.internalStream('uint8array');
    const settle = (error?: unknown) => {
      if (settled) return;
      settled = true;
      if (error === undefined) resolve();
      else if (error instanceof Error) reject(error);
      else reject(new Error('Could not read the sheet.', { cause: error }));
    };
    // jszip doesn't catch what a listener throws, so a malformed record
    // would leave this promise pending; stop and reject instead.
    const guarded = (work: () => void) => {
      try {
        work();
      } catch (error) {
        stream.pause();
        settle(error);
      }
    };
    stream
      .on('data', (chunk: Uint8Array) =>
        guarded(() => {
          if (settled) return;
          const bytes = joinBytes(carry, chunk);
          const used = rows.take(bytes);
          if (rows.done) {
            stream.pause();
            settle();
            return;
          }
          carry = bytes.slice(used);
          if (carry.length > MAX_CARRY_BYTES) {
            throw new Error('A sheet record runs past 1 MiB.');
          }
        }),
      )
      .on('end', () =>
        guarded(() => {
          rows.finish(carry.length);
          settle();
        }),
      )
      .on('error', (error: Error) => settle(error))
      .resume();
  });
}

/**
 * Stream every sheet's rows, in workbook order, to the visitor `openSheet`
 * returns for it. Rows with no cells are not visited. Throws for a file
 * that is not an `.xlsb`.
 */
export async function streamXlsbRows(
  file: Blob,
  openSheet: XlsbSheetOpener,
): Promise<void> {
  const zip = await JSZip.loadAsync(await file.arrayBuffer());
  const [workbook, relsXml, stringsBytes] = await Promise.all([
    zip.file(WORKBOOK_PART)?.async('uint8array'),
    zip.file(WORKBOOK_RELS_PART)?.async('string'),
    zip.file(SHARED_STRINGS_PART)?.async('uint8array'),
  ]);
  if (workbook === undefined || relsXml === undefined) {
    throw new Error('Not an Excel binary workbook (.xlsb).');
  }
  const strings = stringsBytes ? parseSharedStrings(stringsBytes) : [];
  const targets = relationshipTargets(relsXml);
  for (const { name, relId } of parseWorkbookSheets(workbook)) {
    const target = targets.get(relId);
    const entry = target ? zip.file(sheetPath(target)) : null;
    if (!entry) continue;
    const visit = openSheet(name);
    if (!visit) continue;
    // react-doctor-disable-next-line react-doctor/async-await-in-loop -- sheets stream one at a time, in workbook order, to keep one sheet's chunks in memory.
    await scanSheet(entry, new SheetRows(strings, visit));
  }
}
